import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import SmtpSettingsPage from './SmtpSettingsPage';

// Inline wrapper so it works inside a modal
function SmtpSettings({ onClose }) {
  return <SmtpSettingsPage />;
}
import { useAuth } from '../context/AuthContext';

const ROLES = ['superadmin','admin','manager','member','guest','viewer'];
const ROLE_COLORS = {
  superadmin: { bg:'rgba(91,91,214,0.12)', color:'#5B5BD6' },
  admin: { bg:'rgba(37,99,235,0.10)', color:'#2563EB' },
  manager: { bg:'rgba(8,145,178,0.10)', color:'#0891B2' },
  member: { bg:'rgba(22,163,74,0.10)', color:'#16A34A' },
  guest: { bg:'rgba(107,114,128,0.10)', color:'#6B7280' },
  viewer: { bg:'rgba(138,147,166,0.10)', color:'#8A93A6' },
};
const AVATAR_COLORS = ['#5B5BD6','#2563EB','#0891B2','#16A34A','#D97706','#DC2626','#EC4899','#7C3AED','#059669','#DB2777'];

function generatePassword() {
  // Guarantee all 4 character classes are present
  const upper   = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const lower   = 'abcdefghjkmnpqrstuvwxyz';
  const numbers = '23456789';
  const special = '!@#$%&*';
  const all     = upper + lower + numbers + special;
  const rand = (s) => s[Math.floor(Math.random() * s.length)];
  // Start with one guaranteed char from each class
  let chars = [rand(upper), rand(lower), rand(numbers), rand(special)];
  // Fill to 14 chars total
  for (let i = 4; i < 14; i++) chars.push(rand(all));
  // Shuffle so guaranteed chars aren't always at front
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function Avatar({ name, color, size = 34 }) {
  const initials = (name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:color||'#5B5BD6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.34, fontWeight:'700', color:'#fff', flexShrink:0 }}>
      {initials}
    </div>
  );
}

export default function UsersPage() {
  const [pageMsg, setPageMsg] = React.useState(null); // {type:'error'|'success', text}
  const showMsg = (type, text) => { setPageMsg({type, text}); setTimeout(()=>setPageMsg(null), 4000); };
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdUser, setCreatedUser] = useState(null); // shows the new user credentials
  const [newUser, setNewUser] = useState({ full_name:'', username:'', email:'', role:'member', crm_role:'none', ipp_role:'none', finance_role:'none', avatar_color: AVATAR_COLORS[0] });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [manualPassword, setManualPassword] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [resendUser, setResendUser] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  function loadUsers() {
    api.get('/users').then(setUsers).catch(()=>{});
  }

  function deleteUser(uid, uname) {
    setDeleteConfirmUser({ id: uid, name: uname });
  }

  async function confirmDeleteUser() {
    if (!deleteConfirmUser) return;
    const { id: uid } = deleteConfirmUser;
    setDeleteConfirmUser(null);
    try {
      await api.delete('/users/' + uid);
      loadUsers();
    } catch(e) {
      console.error('Delete failed:', e);
      setDeleteError(e?.error || 'Failed to delete user');
      setTimeout(() => setDeleteError(null), 6000);
    }
  }

  const [newPassword, setNewPassword] = useState('');

  const [editSaving, setEditSaving] = React.useState(false);
  const [editSaved, setEditSaved] = React.useState(false);

  async function saveEdit() {
    setEditSaving(true); setEditSaved(false);
    try {
      const payload = { full_name: editUser.full_name, role: editUser.role, is_active: editUser.is_active, crm_role: editUser.crm_role || 'none', ipp_role: editUser.ipp_role || 'none', finance_role: editUser.finance_role || 'none' };
      if (newPassword && newPassword.length >= 12) payload.new_password = newPassword;
      await api.put(`/users/${editUser.id}`, payload);
      setEditSaved(true);
      loadUsers();
      setTimeout(() => { setEditUser(null); setEditSaved(false); }, 900);
    } catch(e) { showMsg('error', e?.error||'Failed'); }
    finally { setEditSaving(false); }
  }

  function openCreateModal() {
    const pwd = generatePassword();
    setGeneratedPassword(pwd);
    setManualPassword(false);
    setNewUser({ full_name:'', username:'', email:'', role:'member', crm_role:'none', ipp_role:'none', finance_role:'none', avatar_color: AVATAR_COLORS[Math.floor(Math.random()*AVATAR_COLORS.length)] });
    setShowCreateModal(true);
  }

  function resendWelcome(u) {
    if (!u.email) { showMsg('error', `${u.full_name} has no email address on file.`); return; }
    setResendUser(u);
  }
  async function doResendWelcome() {
    const u = resendUser; if (!u) return;
    setResendingId(u.id); setResendUser(null);
    try {
      const r = await api.post(`/users/${u.id}/resend-welcome`);
      showMsg('success', r?.welcome_sent ? `Welcome email sent to ${u.email}.` : 'Done.');
    } catch(e) { showMsg('error', e?.error || 'Could not send welcome email.'); }
    finally { setResendingId(null); }
  }

  async function createUser() {
    if (!newUser.full_name.trim() || !newUser.username.trim() || !newUser.email.trim()) {
      showMsg('error','Please fill all required fields'); return;
    }
    try {
      await api.post('/users', { ...newUser, password: generatedPassword, send_welcome: sendWelcome });
      setShowCreateModal(false);
      setCreatedUser({ ...newUser, password: generatedPassword });
      loadUsers();
    } catch(e) { showMsg('error', e?.error||'Failed to create user'); }
  }

  function copyCredentials() {
    const text = `EOMP Login Credentials\n\nUsername: ${createdUser.username}\nEmail: ${createdUser.email}\nPassword: ${createdUser.password}\n\nPlease change your password after first login.`;
    navigator.clipboard.writeText(text).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false), 2000); });
  }

  const isSuperAdmin = user?.role === 'superadmin';

  const [backupLoading, setBackupLoading] = React.useState(false);
  const [showSmtpSettings, setShowSmtpSettings] = React.useState(false);
  const [backupMsg, setBackupMsg] = React.useState('');

  const [logsTab, setLogsTab] = React.useState('login');
  const [logs, setLogs] = React.useState([]);
  const [logsLoading, setLogsLoading] = React.useState(false);
  const [showLogs, setShowLogs] = React.useState(false);
  const [lastBackup, setLastBackup] = React.useState(null);

  React.useEffect(() => {
    if (isSuperAdmin) {
      fetch('/api/admin/last-backup', { credentials:'include' })
        .then(r=>r.json()).then(d=>setLastBackup(d.last_backup)).catch(()=>{});
    }
  }, [isSuperAdmin]);

  async function loadLogs(type) {
    setLogsLoading(true);
    try {
      const token2 = localStorage.getItem('flowup_token');
      const r = await fetch(`/api/admin/logs?type=${type}&limit=1000`, {
        credentials:'include',
        headers: token2 ? { 'Authorization': 'Bearer '+token2 } : {}
      });
      const d = await r.json();
      setLogs(d.rows || []);
    } catch(e) { setLogs([]); }
    finally { setLogsLoading(false); }
  }

  function downloadLogsPDF(type) {
    const esc = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const title = type === 'login' ? 'Login Activity Log' : 'Audit Log';
    const dateStr = new Date().toLocaleString('en-GB');
    
    let rows = '';
    if (type === 'login') {
      logs.forEach((r,i) => {
        const bg = i%2===0?'#FFFFFF':'#F8FAFC';
        const result = r.success ? '<span style="background:#DCFCE7;color:#16A34A;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700">✓ Success</span>'
                                 : '<span style="background:#FEE2E2;color:#DC2626;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700">✗ Failed</span>';
        rows += `<tr style="background:${bg}">
          <td>${esc(r.created_at?new Date(r.created_at).toLocaleString('en-GB'):'')}</td>
          <td><strong>${esc(r.full_name||'—')}</strong></td>
          <td style="font-family:monospace;font-size:11px">${esc(r.username||'')}</td>
          <td>${result}</td>
        </tr>`;
      });
    } else {
      logs.forEach((r,i) => {
        const bg = i%2===0?'#FFFFFF':'#F8FAFC';
        const status = r.status==='success'
          ? '<span style="background:#DCFCE7;color:#16A34A;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700">Success</span>'
          : '<span style="background:#FEE2E2;color:#DC2626;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700">' + esc(r.status||'error') + '</span>';
        rows += `<tr style="background:${bg}">
          <td>${esc(r.created_at?new Date(r.created_at).toLocaleString('en-GB'):'')}</td>
          <td><strong>${esc(r.username||'—')}</strong></td>
          <td>${esc(r.action||'—')}</td>
          <td style="color:#64748B;font-size:11px">${esc(r.resource_name||r.resource_type||'—')}</td>
          <td>${status}</td>
        </tr>`;
      });
    }
    
    const headers = type === 'login'
      ? '<tr><th>Timestamp</th><th>Full Name</th><th>Username</th><th>Result</th></tr>'
      : '<tr><th>Timestamp</th><th>User</th><th>Action</th><th>Resource</th><th>Status</th></tr>';

    const html = `<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>${title}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;background:#F8FAFC;padding:24px;color:#1E293B}
        @media print{@page{size:A3 landscape;margin:8mm}.no-print{display:none!important}body{padding:8px;background:white;-webkit-print-color-adjust:exact;print-color-adjust:exact}}
        .no-print{display:flex;gap:8px;margin-bottom:16px;justify-content:flex-end}
        .btn{padding:8px 20px;background:#5B5BD6;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700}
        .btn-close{background:#E2E8F0;color:#374151}
        .card{background:white;border-radius:12px;padding:28px;box-shadow:0 2px 16px rgba(0,0,0,.06);max-width:1200px;margin:0 auto}
        h1{font-size:20px;font-weight:800;margin-bottom:4px}
        .meta{font-size:12px;color:#64748B;margin-bottom:20px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        thead th{background:#1E293B;color:white;padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
        td{padding:9px 12px;border-bottom:1px solid #F1F5F9}
        .count{display:inline-block;background:#EEF2FF;color:#5B5BD6;padding:2px 10px;border-radius:99px;font-size:11px;font-weight:700;margin-left:8px}
        .footer{margin-top:20px;padding-top:12px;border-top:1px solid #E2E8F0;font-size:11px;color:#94A3B8;text-align:center}
      </style>
    </head><body>
    <div class="no-print">
      <button class="btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
      <button class="btn btn-close" onclick="window.close()">✕ Close</button>
    </div>
    <div class="card">
      <h1>${title} <span class="count">${logs.length} records</span></h1>
      <div class="meta">Generated: ${dateStr} · Space Communication Technologies</div>
      <table><thead>${headers}</thead><tbody>${rows}</tbody></table>
      <div class="footer">Enterprise Operations Management Platform (EOMP) · Confidential · SCT</div>
    </div>
    </body></html>`;
    
    const w = window.open('','_blank','width=1280,height=900');
    if(w){ w.document.write(html); w.document.close(); }
  }

  function openLogs(tab) {
    setLogsTab(tab); setShowLogs(true); setLogs([]);
    loadLogs(tab);
  }

  async function downloadBackup() {
    setBackupLoading(true); setBackupMsg('');
    try {
      const token = localStorage.getItem('flowup_token');
      const res = await fetch('/api/admin/backup', {
        method:'POST',
        credentials:'include',
        headers: token ? { 'Authorization': 'Bearer '+token } : {}
      });
      if (!res.ok) { const e=await res.json(); setBackupMsg('❌ '+(e.error||'Failed')); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `trajectory-backup-${new Date().toISOString().slice(0,10)}.sql`;
      a.click();
      setBackupMsg('✅ Backup downloaded'); setTimeout(()=>setBackupMsg(''),5000);
    } catch(e) { setBackupMsg('❌ '+e.message); }
    finally { setBackupLoading(false); }
  }



  return (
    <div style={{ flex:1, overflowY:'auto', padding:'28px 32px', background:'var(--bg)' }}>
      {pageMsg&&(
        <div style={{position:'fixed',bottom:'24px',left:'50%',transform:'translateX(-50%)',zIndex:9999,background:pageMsg.type==='error'?'#DC2626':'#16A34A',color:'#fff',padding:'12px 24px',borderRadius:'12px',fontSize:'13px',fontWeight:'600',boxShadow:'0 8px 32px rgba(0,0,0,0.3)',display:'flex',alignItems:'center',gap:'12px',minWidth:'280px'}}>
          <span>{pageMsg.type==='error'?'⚠️':'✅'}</span>
          <span style={{flex:1}}>{pageMsg.text}</span>
          <button onClick={()=>setPageMsg(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',cursor:'pointer',fontSize:'18px',padding:'0',lineHeight:1}}>✕</button>
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'24px' }}>
        <div>
          <h1 style={{ fontSize:'20px', fontWeight:'800', color:'var(--text)', letterSpacing:'-0.4px' }}>Team Members</h1>
          <p style={{ color:'var(--muted)', fontSize:'13px', marginTop:'2px' }}>{users.length} members in workspace</p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            Add Member
          </button>
        )}
      </div>

      {/* Backup Card — superadmin only */}
      {isSuperAdmin && (
        <div style={{background:'linear-gradient(135deg,#1E293B,#334155)',borderRadius:'12px',padding:'18px 24px',marginBottom:'20px',display:'flex',alignItems:'center',gap:'16px',boxShadow:'0 4px 20px rgba(0,0,0,.12)'}}>
          <div style={{fontSize:'28px'}}>🗄️</div>
          <div style={{flex:1}}>
            <div style={{fontSize:'14px',fontWeight:'800',color:'white',marginBottom:'2px'}}>Database Backup</div>
            <div style={{fontSize:'11px',color:'#94A3B8'}}>Download a full SQL backup — all projects, tasks, users, timesheets, and payment data.</div>
            {lastBackup && <div style={{fontSize:'10px',color:'#64748B',marginTop:'3px'}}>Last backup: {new Date(lastBackup).toLocaleString('en-GB')}</div>}
            {backupMsg&&<div style={{fontSize:'12px',marginTop:'5px',fontWeight:'700',color:backupMsg.startsWith('✅')?'#34D399':'#F87171'}}>{backupMsg}</div>}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px',alignItems:'flex-end'}}>
            <button onClick={downloadBackup} disabled={backupLoading}
              style={{padding:'9px 20px',background:backupLoading?'#475569':'#5B5BD6',color:'white',border:'none',borderRadius:'8px',cursor:backupLoading?'not-allowed':'pointer',fontSize:'12px',fontWeight:'700',display:'flex',alignItems:'center',gap:'7px',whiteSpace:'nowrap',opacity:backupLoading?.7:1}}>
              {backupLoading?<><span style={{display:'inline-block',width:12,height:12,border:'2px solid white',borderTopColor:'transparent',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>Backing up…</>:'⬇️ Download Backup'}
            </button>
            <div style={{display:'flex',gap:'6px'}}>
              <button onClick={()=>setShowSmtpSettings(true)}
                style={{padding:'6px 12px',background:'rgba(255,255,255,0.1)',color:'white',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>
                ⚙️ Email & Notifications
              </button>
              <button onClick={()=>openLogs('login')} style={{padding:'6px 12px',background:'rgba(255,255,255,0.1)',color:'white',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>📋 Login Logs</button>
              <button onClick={()=>openLogs('audit')} style={{padding:'6px 12px',background:'rgba(255,255,255,0.1)',color:'white',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>🔍 Audit Log</button>
            </div>
          </div>
        </div>
      )}


      {/* ── SMTP Settings Modal */}
      {showSmtpSettings && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'var(--surface)',borderRadius:'16px',width:'680px',maxWidth:'95vw',maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:'17px',fontWeight:'800',color:'var(--text)'}}>⚙️ Email (SMTP) Settings</div>
              <button onClick={()=>setShowSmtpSettings(false)} style={{padding:'6px 10px',background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:'18px'}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:'auto'}}>
              <SmtpSettings onClose={()=>setShowSmtpSettings(false)}/>
            </div>
          </div>
        </div>
      )}

      {/* ── Logs Modal ─────────────────────────────────────────────────── */}
      {showLogs && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'var(--surface)',borderRadius:'16px',width:'900px',maxWidth:'95vw',maxHeight:'85vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            {/* Header */}
            <div style={{padding:'20px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'12px'}}>
              <div style={{fontSize:'20px'}}>{logsTab==='login'?'📋':'🔍'}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:'16px',fontWeight:'800',color:'var(--text)'}}>{logsTab==='login'?'Login Activity Log':'Audit Log'}</div>
                <div style={{fontSize:'11px',color:'var(--muted)'}}>{logs.length} records</div>
              </div>
              <div style={{display:'flex',gap:'8px'}}>
                <button onClick={()=>{setLogsTab('login');loadLogs('login');}} style={{padding:'6px 14px',background:logsTab==='login'?'#5B5BD6':'var(--surface2)',color:logsTab==='login'?'white':'var(--text)',border:'1px solid var(--border)',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>Login</button>
                <button onClick={()=>{setLogsTab('audit');loadLogs('audit');}} style={{padding:'6px 14px',background:logsTab==='audit'?'#5B5BD6':'var(--surface2)',color:logsTab==='audit'?'white':'var(--text)',border:'1px solid var(--border)',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>Audit</button>
              </div>
              <button onClick={()=>downloadLogsPDF(logsTab)} style={{padding:'7px 14px',background:'#5B5BD6',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>🖨️ PDF</button>
              <button onClick={()=>setShowLogs(false)} style={{padding:'6px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'18px',color:'var(--muted)'}}>✕</button>
            </div>
            {/* Table */}
            <div style={{flex:1,overflowY:'auto'}}>
              {logsLoading ? (
                <div style={{padding:'40px',textAlign:'center',color:'var(--muted)'}}>Loading…</div>
              ) : logs.length === 0 ? (
                <div style={{padding:'40px',textAlign:'center',color:'var(--muted)'}}>No records found</div>
              ) : logsTab === 'login' ? (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead style={{position:'sticky',top:0,background:'var(--surface2)',zIndex:1}}>
                    <tr>{['Time','User','Username','Result'].map(h=>(
                      <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.7px',borderBottom:'1px solid var(--border)'}}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {logs.map((r,i)=>(
                      <tr key={r.id} style={{background:i%2===0?'transparent':'var(--surface2)'}}>
                        <td style={{padding:'9px 14px',color:'var(--muted)',whiteSpace:'nowrap'}}>{r.created_at?new Date(r.created_at).toLocaleString('en-GB'):''}</td>
                        <td style={{padding:'9px 14px',fontWeight:'600',color:'var(--text)'}}>{r.full_name||'—'}</td>
                        <td style={{padding:'9px 14px',color:'var(--muted)',fontFamily:'monospace'}}>{r.username}</td>
                        <td style={{padding:'9px 14px'}}>
                          <span style={{padding:'2px 10px',borderRadius:'99px',fontSize:'10px',fontWeight:'700',background:r.success?'#dcfce7':'#fee2e2',color:r.success?'#16A34A':'#DC2626'}}>
                            {r.success?'✓ Success':'✗ Failed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead style={{position:'sticky',top:0,background:'var(--surface2)',zIndex:1}}>
                    <tr>{['Time','User','Action','Resource','Status'].map(h=>(
                      <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.7px',borderBottom:'1px solid var(--border)'}}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {logs.map((r,i)=>(
                      <tr key={r.id} style={{background:i%2===0?'transparent':'var(--surface2)'}}>
                        <td style={{padding:'9px 14px',color:'var(--muted)',whiteSpace:'nowrap'}}>{r.created_at?new Date(r.created_at).toLocaleString('en-GB'):''}</td>
                        <td style={{padding:'9px 14px',fontWeight:'600',color:'var(--text)'}}>{r.username||'—'}</td>
                        <td style={{padding:'9px 14px',color:'var(--text)'}}>{r.action||'—'}</td>
                        <td style={{padding:'9px 14px',color:'var(--muted)',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.resource_name||r.resource_type||'—'}</td>
                        <td style={{padding:'9px 14px'}}>
                          <span style={{padding:'2px 10px',borderRadius:'99px',fontSize:'10px',fontWeight:'700',background:r.status==='success'?'#dcfce7':'#fee2e2',color:r.status==='success'?'#16A34A':'#DC2626'}}>
                            {r.status||'success'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card" style={{ overflow:'hidden' }}>
        {/* Table Header */}
        <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 120px 100px 100px', background:'var(--surface2)', borderBottom:'1px solid var(--border)', padding:'0' }}>
          {['Member', 'Email / Username', 'Role', 'Status', 'Actions'].map(h => (
            <div key={h} style={{ padding:'10px 16px', fontSize:'11px', fontWeight:'700', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.7px' }}>{h}</div>
          ))}
        </div>

        {users.map((u, idx) => {
          const role = ROLE_COLORS[u.role] || ROLE_COLORS.member;
          return (
            <div key={u.id} style={{ display:'grid', gridTemplateColumns:'2fr 2fr 120px 100px 100px', borderBottom: idx < users.length-1 ? '1px solid var(--border)' : 'none', alignItems:'center', transition:'background .1s' }}
              onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                <Avatar name={u.full_name} color={u.avatar_color} size={34}/>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:'600', color:'var(--text)' }}>{u.full_name}</div>
                  {u.id === user.id && <div style={{ fontSize:'10px', color:'var(--brand)', fontWeight:'600' }}>You</div>}
                </div>
              </div>
              <div style={{ padding:'12px 16px' }}>
                <div style={{ fontSize:'12px', color:'var(--text)', fontWeight:'500' }}>{u.email}</div>
                <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'1px' }}>@{u.username}</div>
              </div>
              <div style={{ padding:'12px 16px' }}>
                <span style={{ fontSize:'11px', fontWeight:'700', padding:'3px 10px', borderRadius:'99px', background:role.bg, color:role.color }}>
                  {u.role}
                </span>
                {u.crm_role && u.crm_role !== 'none' && (
                  <div style={{ marginTop:'5px' }}>
                    <span style={{ fontSize:'10px', fontWeight:'700', padding:'2px 8px', borderRadius:'99px', background:'rgba(13,148,136,0.12)', color:'#0D9488' }}>
                      {u.crm_role === 'crm_admin' ? 'CRM Admin' : 'CRM Member'}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ padding:'12px 16px' }}>
                <span style={{ fontSize:'11px', fontWeight:'600', padding:'3px 10px', borderRadius:'99px', background:u.is_active?'rgba(22,163,74,0.10)':'rgba(220,38,38,0.10)', color:u.is_active?'#16A34A':'#DC2626' }}>
                  {u.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div style={{ padding:'12px 16px' }}>
                {isSuperAdmin && u.id !== user.id && (
                  <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                    <button className="btn btn-sm" onClick={()=>setEditUser({...u})}>Edit</button>
                    <button className="btn btn-sm" onClick={()=>resendWelcome(u)} disabled={resendingId===u.id}
                      title="Generate a new temporary password and email the welcome message to this user">
                      {resendingId===u.id ? 'Sending…' : '✉ Resend'}</button>
                    <button onClick={()=>deleteUser(u.id, u.full_name)}
                      style={{padding:'3px 8px',fontSize:'11px',background:'none',border:'1px solid #FCA5A5',borderRadius:'5px',cursor:'pointer',color:'#DC2626',fontWeight:'700'}}
                      title="Delete user">🗑</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* CREATE USER MODAL */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowCreateModal(false)}}>
          <div className="modal" style={{ width:'480px' }}>
            <h3 style={{ fontSize:'16px', fontWeight:'700', marginBottom:'6px' }}>Add New Member</h3>
            <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'20px' }}>A secure password will be auto-generated. Share it with the member securely.</p>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Full Name *</label>
                <input className="input" value={newUser.full_name} onChange={e=>setNewUser({...newUser,full_name:e.target.value})} placeholder="e.g. Ahmed Al Rashidi" autoFocus/>
              </div>
              <div className="form-group">
                <label className="form-label">Username *</label>
                <input className="input" value={newUser.username} onChange={e=>setNewUser({...newUser,username:e.target.value.toLowerCase().replace(/\s/g,'')})} placeholder="e.g. ahmed.r"/>
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="input" type="email" value={newUser.email} onChange={e=>setNewUser({...newUser,email:e.target.value})} placeholder="e.g. ahmed@omansat.om"/>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="select" value={newUser.role} onChange={e=>setNewUser({...newUser,role:e.target.value})}>
                  {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">CRM Role <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(access to the CRM)</span></label>
                <select className="select" value={newUser.crm_role} onChange={e=>setNewUser({...newUser,crm_role:e.target.value})}>
                  <option value="none">None — no CRM access</option>
                  <option value="crm_member">CRM Member</option>
                  <option value="crm_admin">CRM Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Performance Plans Role <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(access to IPP)</span></label>
                <select className="select" value={newUser.ipp_role} onChange={e=>setNewUser({...newUser,ipp_role:e.target.value})}>
                  <option value="none">None — no IPP access</option>
                  <option value="ipp_member">IPP Member</option>
                  <option value="ipp_admin">IPP Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Finance Requests Role <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(IOU &amp; expense requests)</span></label>
                <select className="select" value={newUser.finance_role} onChange={e=>setNewUser({...newUser,finance_role:e.target.value})}>
                  <option value="none">None — no access</option>
                  <option value="finance_member">Member</option>
                  <option value="finance_manager">Finance Manager</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Avatar Color</label>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', paddingTop:'4px' }}>
                  {AVATAR_COLORS.map(c=>(
                    <div key={c} onClick={()=>setNewUser({...newUser,avatar_color:c})}
                      style={{ width:24, height:24, borderRadius:'50%', background:c, cursor:'pointer', outline:newUser.avatar_color===c?`3px solid ${c}`:'none', outlineOffset:'2px' }}/>
                  ))}
                </div>
              </div>
            </div>

            {/* Password section */}
            <div style={{ background:'rgba(22,163,74,0.07)', border:'1px solid rgba(22,163,74,0.2)', borderRadius:'var(--radius-lg)', padding:'14px 16px', marginBottom:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:'#16A34A', textTransform:'uppercase', letterSpacing:'.7px' }}>🔑 Password</div>
                <button className="btn btn-sm" style={{fontSize:'10px'}} onClick={()=>setManualPassword(p=>!p)}>
                  {manualPassword ? '⚡ Auto-Generate' : '✏ Set Manually'}
                </button>
              </div>
              {manualPassword ? (
                <div>
                  <input className="input" type="text" value={generatedPassword}
                    onChange={e=>setGeneratedPassword(e.target.value)}
                    placeholder="Min 12 chars, upper, lower, number, symbol"
                    style={{fontFamily:'var(--font-mono)',letterSpacing:'1px'}}/>
                  <p style={{fontSize:'10px',color:'var(--muted)',marginTop:'4px'}}>Must be 12+ chars with uppercase, lowercase, number, and special character.</p>
                </div>
              ) : (
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <code style={{ flex:1, fontSize:'15px', fontWeight:'700', color:'var(--text)', fontFamily:'var(--font-mono)', letterSpacing:'2px', background:'var(--surface)', padding:'8px 12px', borderRadius:'var(--radius)', border:'1px solid var(--border)' }}>
                      {generatedPassword}
                    </code>
                    <button className="btn btn-sm" onClick={()=>setGeneratedPassword(generatePassword())}>↻ New</button>
                  </div>
                  <p style={{ fontSize:'11px', color:'var(--muted)', marginTop:'8px' }}>⚠ Save this password — you won't see it again after closing this modal.</p>
                </div>
              )}
            </div>

            <label style={{ display:'flex', alignItems:'flex-start', gap:'9px', marginBottom:'16px', cursor:'pointer', padding:'11px 13px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
              <input type="checkbox" checked={sendWelcome} onChange={e=>setSendWelcome(e.target.checked)} style={{ marginTop:'2px' }} />
              <span>
                <span style={{ fontSize:'13px', fontWeight:'600', color:'var(--text)' }}>Send welcome email</span>
                <span style={{ display:'block', fontSize:'11px', color:'var(--muted)', marginTop:'2px' }}>Emails the user their username, this temporary password, a sign-in link and the training guide. They'll be asked to change the password on first login.</span>
              </span>
            </label>

            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px' }}>
              <Avatar name={newUser.full_name||'?'} color={newUser.avatar_color} size={36}/>
              <div>
                <div style={{ fontSize:'13px', fontWeight:'600', color:'var(--text)' }}>{newUser.full_name||'Preview'}</div>
                <div style={{ fontSize:'11px', color:'var(--muted)' }}>{newUser.role} · {newUser.email||'email'}</div>
              </div>
            </div>

            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', paddingTop:'16px', borderTop:'1px solid var(--border)' }}>
              <button className="btn" onClick={()=>setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createUser}>Create Member</button>
            </div>
          </div>
        </div>
      )}

      {/* CREDENTIALS SUCCESS MODAL */}
      {createdUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:'460px', textAlign:'center' }}>
            <div style={{ fontSize:'40px', marginBottom:'12px' }}>🎉</div>
            <h3 style={{ fontSize:'17px', fontWeight:'700', marginBottom:'6px' }}>Member Created!</h3>
            <p style={{ fontSize:'13px', color:'var(--muted)', marginBottom:'20px' }}>Share these credentials with <strong>{createdUser.full_name}</strong> securely.</p>

            <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'16px', textAlign:'left', marginBottom:'16px' }}>
              <div style={{ display:'grid', gap:'8px' }}>
                {[['Full Name', createdUser.full_name], ['Username', createdUser.username], ['Email', createdUser.email], ['Password', createdUser.password], ['Role', createdUser.role]].map(([k,v])=>(
                  <div key={k} style={{ display:'flex', gap:'12px' }}>
                    <span style={{ fontSize:'11px', fontWeight:'700', color:'var(--muted)', textTransform:'uppercase', width:'70px', flexShrink:0 }}>{k}</span>
                    <span style={{ fontSize:'13px', fontWeight: k==='Password'?'700':'500', color:'var(--text)', fontFamily:k==='Password'?'var(--font-mono)':'inherit', letterSpacing:k==='Password'?'1px':'normal' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display:'flex', gap:'8px', justifyContent:'center' }}>
              <button className="btn btn-primary" onClick={copyCredentials}>
                {copied ? '✅ Copied!' : '📋 Copy Credentials'}
              </button>
              <button className="btn" onClick={()=>{ setCreatedUser(null); setCopied(false); }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {resendUser && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setResendUser(null)}}>
          <div className="modal" style={{ width:'440px' }}>
            <h3 style={{ margin:'0 0 6px', fontSize:'17px', fontWeight:'700' }}>Send welcome email</h3>
            <p style={{ fontSize:'13px', color:'var(--muted)', margin:'0 0 16px', lineHeight:1.5 }}>
              This will email <b style={{color:'var(--text)'}}>{resendUser.full_name}</b> at <b style={{color:'var(--text)'}}>{resendUser.email}</b> their login details and the training guide.
            </p>
            <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:'8px', padding:'11px 14px', marginBottom:'18px' }}>
              <div style={{ fontSize:'12.5px', color:'#92400E', fontWeight:'700', marginBottom:'2px' }}>⚠ A new temporary password will be generated</div>
              <div style={{ fontSize:'12px', color:'#78350F', lineHeight:1.45 }}>Their current password will stop working. They'll set a new one on first login.</div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
              <button className="btn" onClick={()=>setResendUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={doResendWelcome}>Send welcome email</button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setEditUser(null)}}>
          <div className="modal" style={{ width:'400px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px' }}>
              <Avatar name={editUser.full_name} color={editUser.avatar_color} size={38}/>
              <div>
                <h3 style={{ fontSize:'15px', fontWeight:'700' }}>{editUser.full_name}</h3>
                <div style={{ fontSize:'11px', color:'var(--muted)' }}>@{editUser.username}</div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="input" value={editUser.full_name} onChange={e=>setEditUser({...editUser,full_name:e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="select" value={editUser.role} onChange={e=>setEditUser({...editUser,role:e.target.value})}>
                {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">CRM Role <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(access to the CRM)</span></label>
              <select className="select" value={editUser.crm_role||'none'} onChange={e=>setEditUser({...editUser,crm_role:e.target.value})}>
                <option value="none">None — no CRM access</option>
                <option value="crm_member">CRM Member</option>
                <option value="crm_admin">CRM Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Performance Plans Role <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(access to IPP)</span></label>
              <select className="select" value={editUser.ipp_role||'none'} onChange={e=>setEditUser({...editUser,ipp_role:e.target.value})}>
                <option value="none">None — no IPP access</option>
                <option value="ipp_member">IPP Member</option>
                <option value="ipp_admin">IPP Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Finance Requests Role <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(IOU &amp; expense requests)</span></label>
              <select className="select" value={editUser.finance_role||'none'} onChange={e=>setEditUser({...editUser,finance_role:e.target.value})}>
                <option value="none">None — no access</option>
                <option value="finance_member">Member — creates &amp; sees own requests; can be chosen as an approver</option>
                <option value="finance_manager">Finance Manager — sees everything &amp; gives final approval</option>
              </select>
            </div>
            <div className="form-group">
              <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}>
                <input type="checkbox" checked={!!editUser.is_active} onChange={e=>setEditUser({...editUser,is_active:e.target.checked})}/>
                <span style={{ fontSize:'13px', color:'var(--muted2)' }}>Account Active</span>
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">New Password <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'11px'}}>(leave blank to keep current)</span></label>
              <input className="input" type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)}
                placeholder="Min 12 chars, upper, lower, number, symbol" autoComplete="new-password"/>
              {newPassword.length>0&&newPassword.length<12&&<p style={{fontSize:'11px',color:'#DC2626',marginTop:'4px'}}>At least 12 characters required</p>}
            </div>
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', paddingTop:'14px', borderTop:'1px solid var(--border)' }}>
              <button className="btn" onClick={()=>{setEditUser(null);setNewPassword('');}} >Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={newPassword.length>0&&newPassword.length<12}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ────────────────────────────────── */}
      {deleteConfirmUser && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'var(--surface)',borderRadius:'14px',padding:'28px',width:'420px',maxWidth:'95vw',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}>
              <div style={{width:44,height:44,borderRadius:'10px',background:'rgba(220,38,38,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0}}>🗑</div>
              <div>
                <div style={{fontSize:'15px',fontWeight:'800',color:'var(--text)'}}>Delete User</div>
                <div style={{fontSize:'12px',color:'var(--muted)',marginTop:'2px'}}>This cannot be undone</div>
              </div>
            </div>
            <div style={{background:'rgba(220,38,38,0.06)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'8px',padding:'12px 14px',marginBottom:'20px',fontSize:'13px',color:'var(--text)',lineHeight:'1.6'}}>
              Delete <strong>{deleteConfirmUser.name}</strong>?<br/>
              <span style={{fontSize:'11px',color:'var(--muted)'}}>Their account will be deactivated. All tasks and history are preserved.</span>
            </div>
            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
              <button onClick={()=>setDeleteConfirmUser(null)}
                style={{padding:'8px 18px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>
                Cancel
              </button>
              <button onClick={confirmDeleteUser}
                style={{padding:'8px 20px',background:'#DC2626',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Error Toast ─────────────────────────────────── */}
      {deleteError && (
        <div style={{position:'fixed',bottom:24,right:24,zIndex:9999,background:'#DC2626',color:'white',padding:'12px 20px',borderRadius:'10px',fontSize:'13px',fontWeight:'600',boxShadow:'0 4px 20px rgba(220,38,38,.4)',maxWidth:'380px'}}>
          ❌ {deleteError}
        </div>
      )}

    </div>
  );
}